import React, { useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Switch,
	Typography
} from '@mui/material';
import { CustomizedDataGrid } from '../customized_data_grid';
import DialogueModal from 'components/Modal/DialogModal';
import { Column } from 'react-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteConfirmationDialog from './DeleteModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { VariableRow } from './createAlert';
import VariableModal from './VariableModal';
import { useTheme } from '@mui/material/styles';

const VariableStatusCell = ({ row }: any) => {
  const theme = useTheme();
  const colors = theme.palette;

  return (
    <Chip
      sx={{
        minWidth: 64,
        color: row.isEnabled
          ? `${colors.boxColor.pgmGreen.color} !important`
          : `${colors.boxColor.redTrend.color} !important`,
        backgroundColor: row.isEnabled
          ? `${colors.boxColor.greenTrend.bgColor} !important`
          : `${colors.boxColor.redTrend.bgColor} !important`,
        marginTop: 0.5,
      }}
      label={row.isEnabled ? 'ACTIVE' : 'INACTIVE'}
    />
  );
};

const variableStatusFormatter = ({ row }: any) => (
  <VariableStatusCell row={row} />
);

const VariableActionCell = ({
  row,
  variables,
  setVariables,
  setSelectedVariable,
  setShowVariableModal,
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setOpenDialog(true);
    handleClose();
  };

  const handleToggleStatus = () => {
    setVariables(
      variables.map((vr: any) =>
        vr.uuid === row.uuid
          ? { ...vr, isEnabled: !vr.isEnabled }
          : vr
      )
    );
    handleClose();
  };

  const handleDeleteConfirm = () => {
    setVariables((prevVariables: VariableRow[]) =>
      prevVariables.filter((v: VariableRow) => v.uuid !== row.uuid)
    );
    setOpenDialog(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          aria-label="edit"
          size="small"
          onClick={() => {
            setSelectedVariable(row);
            setShowVariableModal(true);
          }}
          sx={{
            color: `${colors.table.moreIcon} !important`
          }}
        >
          <EditIcon
            fontSize="small"
            sx={{
              color: `${colors.table.moreIcon} !important`
            }}
          />
        </IconButton>

        <IconButton
          aria-label="more"
          size="small"
          onClick={handleClick}
          sx={{
            color: `${colors.table.moreIcon} !important`
          }}
        >
          <MoreVertIcon
            fontSize="small"
            sx={{
              color: `${colors.table.moreIcon} !important`
            }}
          />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                '& .MuiMenu-list': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            },
          }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: `${colors.moreOptionDropdown?.background} !important`,
              color: `${colors.moreOptionDropdown?.menuItemText} !important`,
              borderRadius: '6px',
              boxShadow: `
                -2px 0 6px rgba(0, 0, 0, 0.2),
                2px 0 6px rgba(0, 0, 0, 0.2),
                0 4px 8px rgba(0, 0, 0, 0.25)
              `,
              minWidth: 160,
            },
          }}
        >
          <MenuItem
            onClick={handleToggleStatus}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: `${colors.moreOptionDropdown?.menuItemText} !important`,
              minHeight: '48px !important',
              py: 0,
              display: 'flex',
              alignItems: 'center',

              '&:hover': {
                backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 32,
              }}
            >
              <Switch
                checked={row.isEnabled}
                size="small"
                color="default"
                sx={{
                  pointerEvents: 'none',
                  width: 34,
                  height: 20,
                  padding: 0,

                  '& .MuiSwitch-switchBase': {
                    padding: '3px',
                    color: colors.toggle.thumb,

                    '&.Mui-checked': {
                      transform: 'translateX(14px)',
                      color: colors.toggle.thumb,

                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.toggle.trackOn,
                        opacity: 1,
                      },
                    },

                    '&.Mui-disabled + .MuiSwitch-track': {
                      backgroundColor: colors.toggle.disabledTrack,
                      opacity: 1,
                    },
                  },

                  '& .MuiSwitch-thumb': {
                    width: 14,
                    height: 14,
                    backgroundColor: colors.toggle.thumb,
                    boxShadow: colors.toggle.thumbShadow,
                  },

                  '& .MuiSwitch-track': {
                    borderRadius: '10px',
                    backgroundColor: colors.toggle.trackOff,
                    opacity: 1,
                  },
                }}
              />
            </ListItemIcon>

            <Box component="span">
              {row.isEnabled ? 'Deactivate' : 'Activate'}
            </Box>
          </MenuItem>

          <Divider
            sx={{
              margin: '0 !important',
              backgroundColor: `${colors.widget.text.noChange}`,
            }}
          />

          <MenuItem
            onClick={handleDeleteClick}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
              minHeight: '48px !important',
              py: 0,
              display: 'flex',
              alignItems: 'center',

              '&:hover': {
                backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 32,
              }}
            >
              <DeleteOutlineIcon
                sx={{
                  fontSize: '22px',
                  color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
                }}
              />
            </ListItemIcon>

            <Box
              component="span"
              sx={{
                fontSize: '14px',
                color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
              }}
            >
              Delete
            </Box>
          </MenuItem>
        </Menu>
      </Box>

      <DeleteConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDeleteConfirm}
        identifierName={row.name || ''}
        type="Variable"
      />
    </>
  );
};

const variableActionFormatter =
  (
    variables: VariableRow[],
    setVariables: any,
    setSelectedVariable: any,
    setShowVariableModal: any
  ) =>
  ({ row }: any) =>
    (
      <VariableActionCell
        row={row}
        variables={variables}
        setVariables={setVariables}
        setSelectedVariable={setSelectedVariable}
        setShowVariableModal={setShowVariableModal}
      />
    );



export const VariablesTab = ({ variables = [], setVariables }: any) => {
    const [showVariableModal, setShowVariableModal] = useState(false);
    const defaultColumnVariables = ['Name', 'Type', 'Value', 'Status', 'Action'];
    const [selectedVariable, setSelectedVariable] = useState<VariableRow | null>(null);

    const handleManageVariables = (variable: VariableRow, state: string) => {
        setSelectedVariable(null)
		if(state === 'add'){
			setVariables([...variables, variable])
		}else if(state === 'update'){
			setVariables(variables.map((vr: VariableRow) => {
				return vr.uuid === variable.uuid ? variable : vr
			}))
		}
		setShowVariableModal(false);
	};

    function getColumnsVariable(): Array<Column<VariableRow>> {
		return [
			{
				key: "name",
				name: "Name",
				minWidth: 150,
				width: 308,
			},
			{
				key: "type",
				name: "Type",
				width: 200,
				minWidth: 150,
			},
			{
				key: "value",
				name: "Value",
				minWidth: 95,
			},
			{
				key: 'isEnabled',
				name: 'Status',
				width: 150,
				formatter: variableStatusFormatter,
			},
			{
				key: "action",
				name: "Action",
				formatter: variableActionFormatter(
					variables,
					setVariables,
					setSelectedVariable,
					setShowVariableModal
				),
				width: 80,
			},
		];
	}

    return (
        <Box sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.7 }} color="textLight.main">
                    ⓘ This section is to map multiple variables/labels for the alert.
                </Typography>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowVariableModal(true)}
                >
                    CREATE VARIABLE
                </Button>
            </Box>
            <CustomizedDataGrid
                tableColumns={getColumnsVariable()}
                tableData={variables}
                tableType="Variable"
                defaultColumnNames={defaultColumnVariables}
                listLength={variables?.length || 0}
                isDateRangeFilter={false}
                showExport={false}
                isShowMoreButton={false}
                page={1}
                pageSize={0}
                paginationData={0}
            />
            <DialogueModal
                title={selectedVariable ? 'Edit variable' : 'CREATE VARIABLE'}
                isOpen={showVariableModal}
                handleClose={() => {
                    setShowVariableModal(false);
                    setSelectedVariable(null);
                }}
            >
                <VariableModal
                    selectedVariable={selectedVariable}
                    handleClose={() => {
                        setShowVariableModal(false);
                        setSelectedVariable(null);
                    }}
                    onManageVariables={handleManageVariables}
                />
            </DialogueModal>
        </Box>
    )
}